import React, { useState, useEffect, useCallback } from 'react';
import { usePanel } from '../context/PanelContext';
import './PartyKitHealthPage.css';

const PARTYKIT_HOST = process.env.REACT_APP_PARTYKIT_HOST || 'thetopia-live.lafless.partykit.dev';
const PROTOCOL = PARTYKIT_HOST.includes('localhost') ? 'http' : 'https';

const ADDON_CONFIG = {
  'command-voting': { label: 'Command Voting', path: (slug) => `/party/${slug}` },
  'chunk-delete': { label: 'Chunk Delete', path: (slug) => `/parties/chunk/${slug}` },
  'block-delete': { label: 'Block Delete', path: (slug) => `/parties/block/${slug}` },
};

function stateColor(state) {
  if (state === 'active') return '#55FF55';
  if (state === 'paused') return '#FFAA00';
  if (state === 'intermission') return '#55FFFF';
  return '#8e929c';
}

function stateLabel(state) {
  if (state === 'active') return 'Active';
  if (state === 'paused') return 'Paused';
  if (state === 'intermission') return 'Intermission';
  return 'Idle';
}

export default function PartyKitHealthPage() {
  const { events, loading } = usePanel();
  const [health, setHealth] = useState({});
  const [lastPoll, setLastPoll] = useState(null);

  const liveEvents = events.filter((e) => e.slug && e.webAddon && ADDON_CONFIG[e.webAddon]);

  const pollHealth = useCallback(async () => {
    if (liveEvents.length === 0) {
      setHealth({});
      setLastPoll(new Date());
      return;
    }

    const results = {};
    await Promise.all(
      liveEvents.map(async (event) => {
        const config = ADDON_CONFIG[event.webAddon];
        try {
          const url = `${PROTOCOL}://${PARTYKIT_HOST}${config.path(event.slug)}`;
          const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
          if (res.ok) {
            const data = await res.json();
            results[event.slug] = { status: 'ok', ...data };
          } else {
            results[event.slug] = { status: 'error', code: res.status };
          }
        } catch (err) {
          results[event.slug] = { status: 'unreachable', error: err.message };
        }
      })
    );

    setHealth(results);
    setLastPoll(new Date());
  }, [liveEvents]);

  useEffect(() => {
    pollHealth();
    const interval = setInterval(pollHealth, 10000);
    return () => clearInterval(interval);
  }, [pollHealth]);

  return (
    <div className="pkh-page">
      <div className="pkh-container">
        <div className="pkh-header">
          <h2 className="pkh-title">PartyKit Health</h2>
          {lastPoll && (
            <span className="pkh-last-poll">
              Last checked: {lastPoll.toLocaleTimeString()}
            </span>
          )}
        </div>

        <div className="pkh-host-badge">
          <span className="pkh-host-label">Host</span>
          <span className="pkh-host-value">{PARTYKIT_HOST}</span>
        </div>

        {loading ? (
          <div className="pkh-loading">Loading events...</div>
        ) : liveEvents.length === 0 ? (
          <div className="pkh-empty">
            <p>No events with web addons found.</p>
            <p className="pkh-empty-sub">PartyKit rooms are created when events have a slug and web addon configured.</p>
          </div>
        ) : (
          <div className="pkh-grid">
            {liveEvents.map((event) => {
              const config = ADDON_CONFIG[event.webAddon];
              const data = health[event.slug];

              return (
                <div className="pkh-event-card" key={event.slug}>
                  <div className="pkh-event-header">
                    <span className="pkh-event-name">{event.name?.replace(/[§&][0-9a-fk-or]/gi, '').replace(/<#[0-9a-fA-F]{6}>/g, '') || event.slug}</span>
                    <span className="pkh-event-addon">{config.label}</span>
                  </div>
                  <div className="pkh-room">
                    <div className="pkh-room-header">
                      <span className="pkh-room-name">{event.slug}</span>
                      {!data ? (
                        <span className="pkh-status pkh-status-pending">...</span>
                      ) : data.status === 'ok' ? (
                        <span className="pkh-status pkh-status-ok">OK</span>
                      ) : data.status === 'error' ? (
                        <span className="pkh-status pkh-status-error">{data.code}</span>
                      ) : (
                        <span className="pkh-status pkh-status-down">Down</span>
                      )}
                    </div>
                    {data?.status === 'ok' && (
                      <div className="pkh-room-details">
                        <span className="pkh-detail">
                          <span className="pkh-detail-label">Connections</span>
                          <span className="pkh-detail-value">{data.connections ?? 0}</span>
                        </span>
                        <span className="pkh-detail">
                          <span className="pkh-detail-label">State</span>
                          <span className="pkh-detail-value" style={{ color: stateColor(data.roundState) }}>
                            {stateLabel(data.roundState)}
                          </span>
                        </span>
                        <span className="pkh-detail">
                          <span className="pkh-detail-label">Round</span>
                          <span className="pkh-detail-value">{data.roundNumber ?? 0}</span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
